import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import {
  type CreatePartnerDto,
  CreatePartnerDtoPartnerTypeEnum,
  type RegisterOtpDto,
  Role,
} from "../../../../../../api";
import { Button } from "../../../../../../components/common/button";
import { Input } from "../../../../../../components/common/input";
import { authOtpService } from "../../../../../api_client";
import { handleAxiosError } from "../../../../../../common/error_handling";
import { AppRoutes } from "../../../../../../common/routes/app_routes";
import { showErrorToast } from "../../../../../../utils/toasts";
import {
  useRegisterWizard,
  type CountryCode,
} from "../../../../domain/register-wizard";
import { getGccCountry } from "../../../../data/gcc-countries";
import RegisterWizardLayout from "./register-wizard-layout";
import { CountryPicker } from "./country-picker";
import { DevAutofillButton } from "./dev-autofill-button";
import {
  validateBusinessName,
  validateCrNumber,
  validateTaxNumber,
} from "../../../../../../utils/validation";

type FieldErrors = Partial<Record<"businessName" | "crNumber" | "taxNumber", string>>;

function isUserNotFound(error: unknown): boolean {
  if (!(error instanceof AxiosError) || error.response?.status !== 404) return false;
  const data = error.response.data as { message?: unknown } | undefined;
  return typeof data?.message === "string" && data.message.toLowerCase() === "user not found";
}

export function BusinessDetailsStep() {
  const navigate = useNavigate();
  const {
    signupMethod,
    email,
    phoneNumber,
    fullName,
    password,
    partnerType,
    businessName,
    crNumber,
    taxNumber,
    countryCode,
    registered,
    setBusinessDetails,
    setRegistered,
  } = useRegisterWizard();

  const [localName, setLocalName] = useState(businessName);
  const [localCr, setLocalCr] = useState(crNumber);
  const [localTax, setLocalTax] = useState(taxNumber);
  const [localCountry, setLocalCountry] = useState<CountryCode | "">(
    countryCode,
  );
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});

  const country = getGccCountry(localCountry);
  const isSeller = partnerType === CreatePartnerDtoPartnerTypeEnum.Seller;

  const canSubmit = localName && localCr && localTax && localCountry;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !localCountry || submitting) return;

    const values = {
      businessName: localName.trim(),
      crNumber: localCr.trim(),
      taxNumber: localTax.trim(),
    };
    const nextErrors: FieldErrors = {
      businessName: validateBusinessName(values.businessName) ?? undefined,
      crNumber: validateCrNumber(values.crNumber) ?? undefined,
      taxNumber: validateTaxNumber(values.taxNumber) ?? undefined,
    };
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    const selectedCountry = getGccCountry(localCountry);
    if (!selectedCountry) {
      showErrorToast("Please select a country.");
      return;
    }
    if (!partnerType) {
      showErrorToast("Please choose a partner type.");
      navigate(AppRoutes.CREATE_ACCOUNT_PARTNER_TYPE);
      return;
    }

    setBusinessDetails({ ...values, countryCode: localCountry });

    const register = async () => {
      const partner: CreatePartnerDto = {
        name: fullName,
        email: signupMethod === "email" ? email : undefined,
        companyName: values.businessName,
        crNumber: values.crNumber,
        taxNumber: values.taxNumber,
        contactName: fullName,
        countryId: selectedCountry.odooId,
        partnerType: partnerType as CreatePartnerDtoPartnerTypeEnum,
      };

      const body: RegisterOtpDto = {
        name: fullName,
        role: Role.User,
        partner,
        ...(signupMethod === "phone"
          ? { phoneNumber }
          : { email, password }),
      };

      await authOtpService.otpRegister(body);
      setRegistered(true);
    };

    const verifyRoute =
      signupMethod === "phone"
        ? AppRoutes.CREATE_ACCOUNT_VERIFY_PHONE
        : AppRoutes.CREATE_ACCOUNT_VERIFY_EMAIL;
    const resendIdentifier =
      signupMethod === "phone" ? { phoneNumber } : { email };

    setSubmitting(true);
    try {
      if (registered) {
        try {
          await authOtpService.otpResendVerification(resendIdentifier);
        } catch (error) {
          if (isUserNotFound(error)) {
            await register();
          } else {
            throw error;
          }
        }
      } else {
        await register();
      }
      navigate(verifyRoute);
    } catch (error) {
      showErrorToast(handleAxiosError(error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <RegisterWizardLayout
      title="Business Details"
      progress={{ current: 2, total: isSeller ? 4 : 3 }}
      onBack={() => navigate(AppRoutes.CREATE_ACCOUNT_PARTNER_TYPE)}
    >
      {/* noValidate: our inline messages replace the browser's validation bubbles. */}
      <form onSubmit={submit} noValidate className="flex w-full flex-col gap-6">
        <Input
          label="Business name"
          type="text"
          placeholder="Enter business name"
          autoComplete="organization"
          value={localName}
          onChange={(e) => {
            setLocalName(e.target.value);
            setErrors((prev) => ({ ...prev, businessName: undefined }));
          }}
          error={errors.businessName}
          required
        />
        <Input
          label="Commercial Registration Number"
          type="text"
          placeholder={country?.crPlaceholder ?? "Enter Commercial Registration Number"}
          value={localCr}
          onChange={(e) => {
            setLocalCr(e.target.value);
            setErrors((prev) => ({ ...prev, crNumber: undefined }));
          }}
          error={errors.crNumber}
          required
        />
        <Input
          label="Tax Number"
          type="text"
          placeholder={country?.taxPlaceholder ?? "Enter Tax Number"}
          value={localTax}
          onChange={(e) => {
            setLocalTax(e.target.value);
            setErrors((prev) => ({ ...prev, taxNumber: undefined }));
          }}
          error={errors.taxNumber}
          required
        />
        <CountryPicker value={localCountry} onChange={setLocalCountry} />

        <Button variant="primary" type="submit" fullWidth disabled={!canSubmit || submitting}>
          {submitting ? "Sending code…" : "Continue"}
        </Button>
        <DevAutofillButton
          onClick={() => {
            const suffix = Date.now().toString().slice(-5);
            setLocalName(`Dev Co ${suffix}`);
            setLocalCr(`1010${suffix}`);
            setLocalTax(`30012345670${suffix.slice(-4)}`);
            setLocalCountry("SA");
          }}
        />
      </form>
    </RegisterWizardLayout>
  );
}

export default BusinessDetailsStep;
